/* eslint-disable no-unused-vars */
import React, { useMemo, useState } from "react";
import { TrendingUp, AlertTriangle, Sparkles, Filter, Calendar as CalendarIcon, Plus, CheckCircle2, ListTodo } from "lucide-react";
import api from "../lib/api";

/**
 * Opportunity Dashboard — Vorion Annual Restaurant Gap Opportunity Engine
 *
 * Pulls per-day Festival/Event + AI Score from the Outlet Demand Calendar
 * (`biz_odc_event_*` / `biz_odc_score_*`) and per-outlet capacity + APC from
 * the Occupancy & RevPASH tab (`biz_odc_occ_*`). User fills in Covers On
 * Books / Forecast Walk-ins / (optional) Target Covers per day × meal and
 * the dashboard auto-computes Forecast Covers, Gap Covers, Occupancy %,
 * Revenue Opportunity, Demand Level, and AI Recommendation.
 *
 * Persistence keys (flat, year + outlet scoped):
 *   biz_opp_books_{year}_{outlet}_{month}_{day}_{meal}
 *   biz_opp_walkins_{year}_{outlet}_{month}_{day}_{meal}
 *   biz_opp_target_{year}_{outlet}_{month}_{day}_{meal}
 */

const MONTHS = [
  { id: "jan", label: "Jan",  full: "January",   monthIdx: 0 },
  { id: "feb", label: "Feb",  full: "February",  monthIdx: 1 },
  { id: "mar", label: "Mar",  full: "March",     monthIdx: 2 },
  { id: "apr", label: "Apr",  full: "April",     monthIdx: 3 },
  { id: "may", label: "May",  full: "May",       monthIdx: 4 },
  { id: "jun", label: "Jun",  full: "June",      monthIdx: 5 },
  { id: "jul", label: "Jul",  full: "July",      monthIdx: 6 },
  { id: "aug", label: "Aug",  full: "August",    monthIdx: 7 },
  { id: "sep", label: "Sep",  full: "September", monthIdx: 8 },
  { id: "oct", label: "Oct",  full: "October",   monthIdx: 9 },
  { id: "nov", label: "Nov",  full: "November",  monthIdx: 10 },
  { id: "dec", label: "Dec",  full: "December",  monthIdx: 11 },
];
const MEALS = ["Breakfast", "Lunch", "High Tea", "Dinner", "Midnight", "Custom1", "Custom2"];
const DAY_NAMES = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];

const DEFAULT_OUTLETS = [
  { id: "outlet_kmcy",         name: "KMCY" },
  { id: "outlet_feast",        name: "Feast" },
  { id: "outlet_chingari",     name: "Chingari" },
  { id: "outlet_lobby_lounge", name: "Lobby Lounge" },
];

// ---- helpers ----
const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
const fmt = (v, digits = 0) =>
  Number.isFinite(v) && v !== 0 ? v.toLocaleString(undefined, { maximumFractionDigits: digits }) : "—";
const fmtMoney = (v) => Number.isFinite(v) && v !== 0 ? `₹${fmt(v, 0)}` : "—";
const fmtPct = (v) => Number.isFinite(v) && v > 0 ? `${(v * 100).toFixed(1)}%` : "—";

function daysInMonth(year, monthIdx0) {
  return new Date(year, monthIdx0 + 1, 0).getDate();
}
function mealSlug(meal) { return meal.toLowerCase().replace(/\s+/g, "_"); }

// Read event/score from Demand Calendar (year-scoped, with legacy fallback for settings year).
function readCalendar(data, year, settingsYear, mid, day) {
  const evK = `biz_odc_event_${year}_${mid}_${day}`;
  const scK = `biz_odc_score_${year}_${mid}_${day}`;
  let event = data[evK] || "";
  let score = data[scK];
  if ((!event || score === undefined || score === "") && String(settingsYear) === String(year)) {
    event = event || data[`biz_odc_event_${mid}_${day}`] || "";
    if (score === undefined || score === "") score = data[`biz_odc_score_${mid}_${day}`];
  }
  return { event, score: num(score) };
}

// Demand level from AI Score (matches the Excel mapping)
function demandLevelFromScore(score) {
  if (!Number.isFinite(score) || score <= 0) return "Moderate";
  if (score >= 90) return "Very High";
  if (score >= 75) return "High";
  if (score >= 55) return "Moderate";
  return "Low";
}
function demandBadge(level) {
  switch (level) {
    case "Very High": return "bg-[#EF4444]/20 text-[#FCA5A5] border-[#EF4444]/40";
    case "High":      return "bg-[#10B981]/20 text-[#86EFAC] border-[#10B981]/40";
    case "Moderate":  return "bg-[#F59E0B]/15 text-[#FCD34D] border-[#F59E0B]/40";
    case "Low":       return "bg-[#38BDF8]/15 text-[#7DD3FC] border-[#38BDF8]/40";
    default:          return "bg-zinc-800 text-zinc-400 border-zinc-700";
  }
}
function aiRecommendation(level) {
  if (level === "Very High" || level === "High") return "Protect pricing and maximize APC";
  if (level === "Moderate") return "Upsell reservations + social media push";
  return "Push tactical offers + loyalty campaigns";
}

// Per-outlet × meal capacity & APC pulled from the Occupancy & RevPASH tab.
function readOutletMealEconomics(data, outletId, meal) {
  const slug = mealSlug(meal);
  const seats   = num(data[`biz_odc_occ_${outletId}_${slug}_seats`]);
  const hours   = num(data[`biz_odc_occ_${outletId}_${slug}_hours`]);
  const dur     = num(data[`biz_odc_occ_${outletId}_${slug}_duration`]);
  const covers  = num(data[`biz_odc_occ_${outletId}_${slug}_covers`]);
  const revenue = num(data[`biz_odc_occ_${outletId}_${slug}_revenue`]);
  // Capacity = max turn capacity (seats × hours / duration) when all three set,
  // otherwise fall back to seats so the dashboard still produces useful numbers.
  const capacity =
    (seats > 0 && hours > 0 && dur > 0) ? Math.round(seats * (hours / dur)) :
    seats > 0 ? seats : 0;
  const apc = covers > 0 ? revenue / covers : 0;
  return { capacity, apc };
}

function parseOutlets(data) {
  try {
    const v = data["biz_odc_outlets"];
    if (!v) return DEFAULT_OUTLETS;
    const arr = JSON.parse(v);
    return Array.isArray(arr) && arr.length ? arr : DEFAULT_OUTLETS;
  } catch { return DEFAULT_OUTLETS; }
}

// Default target as a % of capacity by meal — used when user hasn't typed an explicit target.
const DEFAULT_TARGET_PCT = {
  Breakfast: 0.75,
  Lunch:     0.85,
  "High Tea":0.75,
  Dinner:    0.90,
  Midnight:  0.60,
  Custom1:   0.70,
  Custom2:   0.70,
};
const DEFAULT_TASKLIST_LABEL = "Backlog";

// Persistence key builders for this dashboard.
const oppKey = {
  books:   (y, o, m, d, meal) => `biz_opp_books_${y}_${o}_${m}_${d}_${mealSlug(meal)}`,
  walkins: (y, o, m, d, meal) => `biz_opp_walkins_${y}_${o}_${m}_${d}_${mealSlug(meal)}`,
  target:  (y, o, m, d, meal) => `biz_opp_target_${y}_${o}_${m}_${d}_${mealSlug(meal)}`,
};

// ====================================================================
// Main component
// ====================================================================
export default function OpportunityDashboard({ workspaceId, data, locked, onChange }) {
  const settingsYear = useMemo(() => {
    const n = parseInt(String(data["biz_odc_year"] || "").trim(), 10);
    return Number.isFinite(n) && n >= 1900 && n <= 2999 ? n : new Date().getFullYear();
  }, [data]);
  const years = useMemo(() => {
    const set = new Set();
    try {
      const v = data["biz_odc_years"];
      if (v) (JSON.parse(v) || []).forEach((y) => { const n = parseInt(y, 10); if (Number.isFinite(n)) set.add(n); });
    } catch { /* ignore */ }
    for (let i = 0; i < 5; i++) set.add(settingsYear + i);
    return Array.from(set).sort((a, b) => a - b);
  }, [data, settingsYear]);

  const outlets = useMemo(() => parseOutlets(data), [data]);

  const [year, setYear] = useState(settingsYear);
  const [outletId, setOutletId] = useState(outlets[0]?.id || "outlet_kmcy");
  const [activeMonth, setActiveMonth] = useState(new Date().getMonth());
  const [onlyEvents, setOnlyEvents] = useState(false);
  const [onlyGaps, setOnlyGaps] = useState(false);

  // Threshold + task-sync state (persisted threshold so it survives reloads).
  const [threshold, setThreshold] = useState(() => {
    const v = parseFloat(data["biz_opp_threshold"]);
    return Number.isFinite(v) && v > 0 ? v : 25000;
  });
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);   // {created:[], skipped:[], project_id, threshold}
  const [syncError, setSyncError] = useState(null);

  const persistThreshold = (val) => {
    setThreshold(val);
    if (!locked) onChange("biz_opp_threshold", String(val));
  };

  const outlet = outlets.find((o) => o.id === outletId) || outlets[0];

  // ---- compute every row for the selected month ----
  const month = MONTHS[activeMonth];
  const dim = daysInMonth(year, month.monthIdx);

  const rows = useMemo(() => {
    const out = [];
    for (let d = 1; d <= dim; d++) {
      const dateObj = new Date(year, month.monthIdx, d);
      const dayName = DAY_NAMES[dateObj.getDay()];
      const { event, score } = readCalendar(data, year, settingsYear, month.id, d);
      const level = demandLevelFromScore(score);
      MEALS.forEach((meal) => {
        const { capacity, apc } = readOutletMealEconomics(data, outletId, meal);
        const booksRaw   = data[oppKey.books(year, outletId, month.id, d, meal)];
        const walkinsRaw = data[oppKey.walkins(year, outletId, month.id, d, meal)];
        const targetRaw  = data[oppKey.target(year, outletId, month.id, d, meal)];
        const books   = num(booksRaw);
        const walkins = num(walkinsRaw);
        const target  = targetRaw !== undefined && targetRaw !== ""
          ? num(targetRaw)
          : Math.round(capacity * (DEFAULT_TARGET_PCT[meal] || 0.8));
        const forecast = books + walkins;
        const gap = Math.max(0, capacity - forecast);
        const occ = capacity > 0 ? forecast / capacity : 0;
        const revOpp = gap * apc;
        const recommendation = aiRecommendation(level);
        out.push({
          day: d, dayName, meal, event, score, level,
          capacity, target, books, walkins, forecast, gap, occ, apc, revOpp, recommendation,
          hasInput: booksRaw !== undefined || walkinsRaw !== undefined || targetRaw !== undefined,
        });
      });
    }
    return out;
  }, [data, year, outletId, activeMonth, dim, month.id, month.monthIdx, settingsYear]);

  // ---- KPI roll-up (selected month, selected outlet) ----
  const monthKpis = useMemo(() => {
    let cap = 0, forecast = 0, gap = 0, revOpp = 0, eventDays = new Set(), peakDays = 0, lowDays = 0;
    rows.forEach((r) => {
      cap += r.capacity;
      forecast += r.forecast;
      gap += r.gap;
      revOpp += r.revOpp;
      if (r.event) eventDays.add(r.day);
      if (r.level === "Very High" || r.level === "High") peakDays += 1;
      if (r.level === "Low") lowDays += 1;
    });
    const occ = cap > 0 ? forecast / cap : 0;
    return { cap, forecast, gap, revOpp, occ, eventDays: eventDays.size, peakDays, lowDays };
  }, [rows]);

  // ---- Annual roll-up (whole year, selected outlet) ----
  const annualKpis = useMemo(() => {
    let cap = 0, forecast = 0, gap = 0, revOpp = 0;
    MONTHS.forEach((m) => {
      const dimM = daysInMonth(year, m.monthIdx);
      for (let d = 1; d <= dimM; d++) {
        MEALS.forEach((meal) => {
          const { capacity, apc } = readOutletMealEconomics(data, outletId, meal);
          const books   = num(data[oppKey.books(year, outletId, m.id, d, meal)]);
          const walkins = num(data[oppKey.walkins(year, outletId, m.id, d, meal)]);
          const fc = books + walkins;
          const g  = Math.max(0, capacity - fc);
          cap += capacity; forecast += fc; gap += g; revOpp += g * apc;
        });
      }
    });
    const occ = cap > 0 ? forecast / cap : 0;
    return { cap, forecast, gap, revOpp, occ };
  }, [data, year, outletId]);

  // ---- Apply filters for table render ----
  const visibleRows = rows.filter((r) => {
    if (onlyEvents && !r.event) return false;
    if (onlyGaps && r.gap === 0) return false;
    return true;
  });

  const setField = (key, value) => { if (!locked) onChange(key, value); };

  // Bulk action: auto-fill walk-ins for empty days using a heuristic from AI score.
  const autoForecastMonth = () => {
    if (locked) return;
    if (!window.confirm(
      `Auto-forecast walk-ins for empty rows in ${month.full} ${year} (${outlet?.name})? ` +
      `Heuristic: 40-70% of remaining capacity based on the day's AI demand score. ` +
      `Rows that already have a walk-in value will NOT be overwritten.`
    )) return;
    rows.forEach((r) => {
      const existing = data[oppKey.walkins(year, outletId, month.id, r.day, r.meal)];
      if (existing !== undefined && existing !== "") return;
      if (r.capacity <= 0) return;
      const remaining = Math.max(0, r.capacity - r.books);
      const pct = r.score >= 90 ? 0.7 : r.score >= 75 ? 0.55 : r.score >= 55 ? 0.45 : 0.35;
      const guess = Math.round(remaining * pct);
      if (guess > 0) onChange(oppKey.walkins(year, outletId, month.id, r.day, r.meal), String(guess));
    });
  };
  const clearMonth = () => {
    if (locked) return;
    if (!window.confirm(`Clear every Books / Walk-ins / Target value in ${month.full} ${year} for ${outlet?.name}?`)) return;
    rows.forEach((r) => {
      onChange(oppKey.books(year, outletId, month.id, r.day, r.meal), "");
      onChange(oppKey.walkins(year, outletId, month.id, r.day, r.meal), "");
      onChange(oppKey.target(year, outletId, month.id, r.day, r.meal), "");
    });
  };

  // ---- Task-sync helpers ----
  const buildRowPayload = (r) => {
    const dueIso = new Date(Date.UTC(year, month.monthIdx, r.day)).toISOString().slice(0, 10);
    return {
      year,
      outlet_id: outletId,
      outlet_name: outlet?.name || outletId,
      month_id: month.id,
      month_label: month.label,
      day: r.day,
      meal: r.meal,
      event: r.event || "",
      capacity: r.capacity,
      forecast: r.forecast,
      gap: r.gap,
      occ_pct: r.occ,
      rev_opp: r.revOpp,
      demand_level: r.level,
      recommendation: r.recommendation,
      due_date: dueIso,
    };
  };

  const flaggedRows = rows.filter((r) => r.gap > 0 && r.revOpp >= threshold);

  const syncRows = async (payloadRows, label) => {
    if (locked) return;
    if (!workspaceId) {
      setSyncError("Workspace not loaded yet.");
      return;
    }
    if (payloadRows.length === 0) {
      setSyncError(`No flagged rows ${label} (revenue opportunity below ₹${threshold.toLocaleString()}).`);
      return;
    }
    setSyncing(true);
    setSyncError(null);
    setSyncResult(null);
    try {
      const r = await api.post(
        `/workspaces/${workspaceId}/opportunity/sync-tasks`,
        { threshold, rows: payloadRows.map(buildRowPayload) },
      );
      setSyncResult(r.data);
    } catch (err) {
      setSyncError(err?.response?.data?.detail || err?.message || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const syncMonthFlagged = () => {
    if (!window.confirm(
      `Create Kanban tasks for ${flaggedRows.length} flagged row(s) in ${month.full} ${year} ` +
      `(revenue opportunity ≥ ₹${threshold.toLocaleString()})?\n\n` +
      `Tasks land in "Revenue Recovery Pipeline" (auto-created once per workspace) and are assigned to the workspace owner. ` +
      `Duplicates for the same day × meal are skipped.`
    )) return;
    syncRows(flaggedRows, `in ${month.full} ${year}`);
  };

  const syncSingleRow = (r) => {
    if (locked) return;
    syncRows([r], `for ${r.meal} on ${r.day}-${month.label}-${year}`);
  };

  return (
    <div
      className="bg-[#141417] border border-zinc-800 rounded-lg overflow-hidden"
      data-testid="opportunity-dashboard"
    >
      {/* Title bar */}
      <div className="bg-gradient-to-r from-[#0B3D2E] to-[#10302A] px-4 py-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-white text-lg font-semibold tracking-tight flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[#D4A968]" />
            VORION — Annual Restaurant Gap Opportunity Engine
          </h2>
          <p className="text-[#86EFAC]/80 text-[11px] mt-0.5">
            Daily gap analysis pulled from the Outlet Demand Calendar. Fill bookings &amp; walk-ins, the engine surfaces revenue at risk + the right AI play.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value, 10))}
            data-testid="opp-year-select"
            className="bg-[#0B0B0E] border border-zinc-700 text-zinc-100 text-xs rounded px-2 py-1.5 font-mono"
          >
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <select
            value={outletId}
            onChange={(e) => setOutletId(e.target.value)}
            data-testid="opp-outlet-select"
            className="bg-[#0B0B0E] border border-zinc-700 text-zinc-100 text-xs rounded px-2 py-1.5"
          >
            {outlets.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Annual KPI strip */}
        <div data-testid="opp-annual-kpis">
          <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 mb-1.5">
            Annual {year} · {outlet?.name}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <KPI label="Cover-Days Capacity"    value={fmt(annualKpis.cap, 0)} testid="opp-kpi-annual-cap" />
            <KPI label="Forecast Covers"        value={fmt(annualKpis.forecast, 0)} testid="opp-kpi-annual-forecast" />
            <KPI label="Gap Covers"             value={fmt(annualKpis.gap, 0)} highlight="amber" testid="opp-kpi-annual-gap" />
            <KPI label="Occupancy"              value={fmtPct(annualKpis.occ)} testid="opp-kpi-annual-occ" />
            <KPI label="Revenue Opportunity"    value={fmtMoney(annualKpis.revOpp)} highlight="gold" testid="opp-kpi-annual-revopp" />
          </div>
        </div>

        {/* Month tabs */}
        <div className="flex flex-wrap gap-1" role="tablist" data-testid="opp-month-tabs">
          {MONTHS.map((m, i) => {
            const active = i === activeMonth;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => setActiveMonth(i)}
                data-testid={`opp-month-tab-${m.id}`}
                className={`px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest rounded transition-colors ${
                  active
                    ? "bg-[#D4A968] text-[#0B0B0E]"
                    : "bg-[#0B0B0E] border border-zinc-800 text-zinc-400 hover:text-zinc-100 hover:border-zinc-700"
                }`}
              >
                {m.label}
              </button>
            );
          })}
        </div>

        {/* Month KPI strip + actions */}
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="grow min-w-0">
            <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 mb-1.5 flex items-center gap-1.5">
              <CalendarIcon className="w-3 h-3" /> {month.full} {year} · {outlet?.name}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
              <KPI label="Capacity"             value={fmt(monthKpis.cap, 0)} testid="opp-kpi-month-cap" />
              <KPI label="Forecast"             value={fmt(monthKpis.forecast, 0)} testid="opp-kpi-month-forecast" />
              <KPI label="Gap Covers"           value={fmt(monthKpis.gap, 0)} highlight="amber" testid="opp-kpi-month-gap" />
              <KPI label="Occupancy"            value={fmtPct(monthKpis.occ)} testid="opp-kpi-month-occ" />
              <KPI label="Revenue Opp."         value={fmtMoney(monthKpis.revOpp)} highlight="gold" testid="opp-kpi-month-revopp" />
              <KPI label="Event Days · Peak / Low" value={`${monthKpis.eventDays} · ${monthKpis.peakDays} / ${monthKpis.lowDays}`} testid="opp-kpi-month-event-days" />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-zinc-400 cursor-pointer">
              <input
                type="checkbox"
                checked={onlyEvents}
                onChange={(e) => setOnlyEvents(e.target.checked)}
                data-testid="opp-filter-events"
                className="accent-[#D4A968]"
              />
              Event days
            </label>
            <label className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-zinc-400 cursor-pointer">
              <input
                type="checkbox"
                checked={onlyGaps}
                onChange={(e) => setOnlyGaps(e.target.checked)}
                data-testid="opp-filter-gaps"
                className="accent-[#D4A968]"
              />
              Gap &gt; 0
            </label>
            <button
              type="button"
              onClick={autoForecastMonth}
              disabled={locked}
              data-testid="opp-auto-forecast-btn"
              className="text-[10px] font-mono uppercase tracking-widest px-2 py-1.5 rounded bg-[#D4A968]/15 border border-[#D4A968]/40 text-[#D4A968] hover:bg-[#D4A968]/25 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
              title="Auto-suggest walk-ins for empty rows using the day's AI demand score"
            >
              <Sparkles className="w-3 h-3" /> Auto-forecast month
            </button>
            <button
              type="button"
              onClick={clearMonth}
              disabled={locked}
              data-testid="opp-clear-month-btn"
              className="text-[10px] font-mono uppercase tracking-widest px-2 py-1.5 rounded bg-zinc-800 border border-zinc-700 text-zinc-400 hover:text-zinc-100 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Clear month
            </button>
          </div>
        </div>

        {/* Revenue-recovery threshold + bulk sync */}
        <div
          className="bg-[#0B0B0E] border border-[#D4A968]/30 rounded-lg p-3 flex flex-wrap items-center gap-3"
          data-testid="opp-pipeline-bar"
        >
          <div className="flex items-center gap-2">
            <ListTodo className="w-3.5 h-3.5 text-[#D4A968]" />
            <span className="text-[10px] font-mono uppercase tracking-widest text-[#D4A968]">
              Revenue-Recovery Pipeline
            </span>
          </div>
          <label className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-zinc-400">
            Flag rows ≥ ₹
            <input
              type="number"
              min="0"
              step="1000"
              value={threshold}
              onChange={(e) => persistThreshold(Math.max(0, parseFloat(e.target.value) || 0))}
              disabled={locked}
              data-testid="opp-threshold-input"
              className="w-24 bg-[#141417] border border-zinc-800 focus:border-[#D4A968] rounded px-1.5 py-1 text-[11px] text-white text-right tabular-nums outline-none disabled:opacity-60"
            />
          </label>
          <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            <span className="text-zinc-200 tabular-nums">{flaggedRows.length}</span> flagged in {month.label} ·{" "}
            <span className="text-[#D4A968] tabular-nums">{fmtMoney(flaggedRows.reduce((a, b) => a + b.revOpp, 0))}</span> at risk
          </span>
          <button
            type="button"
            onClick={syncMonthFlagged}
            disabled={locked || syncing || flaggedRows.length === 0}
            data-testid="opp-sync-month-btn"
            className="ml-auto text-[10px] font-mono uppercase tracking-widest px-3 py-1.5 rounded bg-[#10B981] text-[#0B0B0E] hover:bg-[#34D399] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
            title="Create one Kanban task per flagged row in the Revenue Recovery Pipeline project (skips duplicates)"
          >
            {syncing ? <Sparkles className="w-3 h-3 animate-pulse" /> : <Plus className="w-3 h-3" />}
            {syncing ? "Syncing…" : `Sync ${flaggedRows.length || "0"} → Tasks`}
          </button>
        </div>

        {/* Sync result banner */}
        {(syncResult || syncError) && (
          <div
            className={`rounded-lg p-3 text-[11px] flex items-start gap-2 ${
              syncError
                ? "bg-[#EF4444]/10 border border-[#EF4444]/30 text-[#FCA5A5]"
                : "bg-[#10B981]/10 border border-[#10B981]/30 text-[#86EFAC]"
            }`}
            data-testid="opp-sync-banner"
          >
            {syncError ? <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                       : <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />}
            <div className="grow min-w-0">
              {syncError ? (
                <span>{syncError}</span>
              ) : (
                <>
                  <span className="font-semibold">
                    Created {syncResult.created.length} task{syncResult.created.length === 1 ? "" : "s"}
                  </span>{" "}
                  in <span className="font-semibold">{syncResult.project_name}</span>
                  {syncResult.skipped.length > 0
                    ? ` · ${syncResult.skipped.length} skipped (already exist)`
                    : ""}
                  {syncResult.created.length > 0 && (
                    <span className="block mt-1 text-zinc-400">
                      Assigned to the workspace owner · Tasks land in the {DEFAULT_TASKLIST_LABEL} column.
                    </span>
                  )}
                </>
              )}
            </div>
            <button
              type="button"
              onClick={() => { setSyncResult(null); setSyncError(null); }}
              className="text-zinc-500 hover:text-zinc-100 text-xs shrink-0"
              data-testid="opp-sync-banner-close"
            >×</button>
          </div>
        )}

        {/* Capacity & APC missing warning */}
        {(() => {
          const eco = MEALS.map((m) => readOutletMealEconomics(data, outletId, m));
          const anyCap = eco.some((e) => e.capacity > 0);
          const anyApc = eco.some((e) => e.apc > 0);
          if (anyCap && anyApc) return null;
          return (
            <div
              className="bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded p-2.5 text-[11px] text-[#FCD34D] flex items-start gap-2"
              data-testid="opp-missing-economics-warning"
            >
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>
                Fill the <span className="font-semibold">Occupancy &amp; RevPASH</span> tab above (seats, hours, duration, covers, revenue) for{" "}
                <span className="font-semibold">{outlet?.name}</span> — that powers Capacity &amp; APC for this engine.
                Currently {anyCap ? "" : "no capacity"}{anyCap || anyApc ? "" : " and "}{anyApc ? "" : "no APC"} is set.
              </span>
            </div>
          );
        })()}

        {/* The big gap-engine table */}
        <div className="rounded-lg overflow-hidden border border-zinc-800" data-testid="opp-table-wrapper">
          <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
            <table className="w-full text-[11px] border-separate border-spacing-0">
              <thead className="bg-[#0B3D2E] text-white sticky top-0 z-10">
                <tr>
                  {[
                    "Date","Day","Meal","Festival / Event","Capacity","Target","Books","Walk-ins","Forecast","Gap","Occ %","Rev Opportunity","Demand","AI Recommendation","Task",
                  ].map((h) => (
                    <th
                      key={h}
                      className="text-left font-mono uppercase tracking-widest px-2 py-1.5 whitespace-nowrap text-[9px] border-b-2 border-[#D4A968]"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleRows.length === 0 && (
                  <tr><td colSpan={15} className="px-3 py-6 text-center text-zinc-500 text-xs">
                    No rows match the active filter.
                  </td></tr>
                )}
                {visibleRows.map((r, idx) => {
                  const prev = idx > 0 ? visibleRows[idx - 1] : null;
                  const newDay = !prev || prev.day !== r.day;
                  const rowBorder = newDay ? "border-t border-zinc-700" : "border-t border-zinc-800/40";
                  return (
                    <tr
                      key={`${r.day}-${r.meal}`}
                      className={`${rowBorder} ${r.event ? "bg-[#10B981]/5" : ""} hover:bg-[#D4A968]/5`}
                      data-testid={`opp-row-${month.id}-${r.day}-${mealSlug(r.meal)}`}
                    >
                      <td className="px-2 py-1.5 whitespace-nowrap text-zinc-300 tabular-nums">
                        {newDay ? `${String(r.day).padStart(2,"0")}-${month.label}` : ""}
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-zinc-400">{newDay ? r.dayName : ""}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-zinc-200">{r.meal}</td>
                      <td className="px-2 py-1.5 max-w-[240px] truncate text-zinc-300" title={r.event}>
                        {r.event || <span className="text-zinc-600">—</span>}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-zinc-300">{r.capacity || "—"}</td>
                      <td className="px-2 py-1.5">
                        <NumCell
                          value={data[oppKey.target(year, outletId, month.id, r.day, r.meal)] ?? ""}
                          placeholder={String(r.target || "")}
                          onChange={(v) => setField(oppKey.target(year, outletId, month.id, r.day, r.meal), v)}
                          locked={locked}
                          testid={`opp-target-${month.id}-${r.day}-${mealSlug(r.meal)}`}
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <NumCell
                          value={data[oppKey.books(year, outletId, month.id, r.day, r.meal)] ?? ""}
                          onChange={(v) => setField(oppKey.books(year, outletId, month.id, r.day, r.meal), v)}
                          locked={locked}
                          testid={`opp-books-${month.id}-${r.day}-${mealSlug(r.meal)}`}
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <NumCell
                          value={data[oppKey.walkins(year, outletId, month.id, r.day, r.meal)] ?? ""}
                          onChange={(v) => setField(oppKey.walkins(year, outletId, month.id, r.day, r.meal), v)}
                          locked={locked}
                          testid={`opp-walkins-${month.id}-${r.day}-${mealSlug(r.meal)}`}
                        />
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-zinc-100 font-semibold">{r.forecast || "—"}</td>
                      <td className={`px-2 py-1.5 text-right tabular-nums ${
                        r.gap > 0 ? "text-[#FCD34D]" : "text-[#86EFAC]"
                      }`}>
                        {r.gap || (r.capacity > 0 ? "0" : "—")}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        <span className={
                          r.occ >= 0.85 ? "text-[#FCA5A5]" :
                          r.occ >= 0.6  ? "text-[#86EFAC]" :
                          r.occ > 0     ? "text-[#FCD34D]" : "text-zinc-500"
                        }>{fmtPct(r.occ)}</span>
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-[#D4A968]">{fmtMoney(r.revOpp)}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">
                        <span className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border ${demandBadge(r.level)}`}>
                          {r.level}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-zinc-300 max-w-[260px]">{r.recommendation}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">
                        {r.gap > 0 && r.revOpp >= threshold ? (
                          <button
                            type="button"
                            onClick={() => syncSingleRow(r)}
                            disabled={locked || syncing}
                            data-testid={`opp-row-task-${month.id}-${r.day}-${mealSlug(r.meal)}`}
                            className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-1 rounded bg-[#10B981]/20 border border-[#10B981]/40 text-[#86EFAC] hover:bg-[#10B981]/30 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
                            title={`Create Kanban task in Revenue Recovery Pipeline (Rev opp ₹${r.revOpp.toLocaleString()})`}
                          >
                            <Plus className="w-2.5 h-2.5" /> Task
                          </button>
                        ) : (
                          <span className="text-zinc-700 text-[10px]">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-3 text-[10px] font-mono uppercase tracking-widest text-zinc-500 pt-1">
          <Filter className="w-3 h-3" />
          <span>Demand Level Legend:</span>
          <LegendDot label="Very High (≥90)" cls={demandBadge("Very High")} />
          <LegendDot label="High (75-89)"     cls={demandBadge("High")} />
          <LegendDot label="Moderate (55-74)" cls={demandBadge("Moderate")} />
          <LegendDot label="Low (<55)"        cls={demandBadge("Low")} />
        </div>
      </div>
    </div>
  );
}

// ---------- tiny presentational helpers ----------
function KPI({ label, value, highlight, testid }) {
  const tone = highlight === "gold"  ? "text-[#D4A968]"
            : highlight === "amber" ? "text-[#FCD34D]"
            : "text-zinc-100";
  return (
    <div className="bg-[#0B0B0E] border border-zinc-800 rounded p-2.5" data-testid={testid}>
      <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`text-base font-semibold tabular-nums mt-0.5 ${tone}`}>{value}</div>
    </div>
  );
}

function NumCell({ value, placeholder, onChange, locked, testid }) {
  return (
    <input
      type="number"
      min="0"
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      disabled={locked}
      data-testid={testid}
      className="w-16 bg-[#0B0B0E] border border-zinc-800 focus:border-[#D4A968] rounded px-1.5 py-1 text-[11px] text-white text-right tabular-nums outline-none disabled:opacity-60 disabled:cursor-not-allowed placeholder:text-zinc-600"
    />
  );
}

function LegendDot({ label, cls }) {
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[9px] ${cls}`}>
      {label}
    </span>
  );
}
